import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MapPin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";
import { PasswordInput } from "@/components/PasswordInput";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100";

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, login, register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  useEffect(() => {
    if (user) navigate(from);
  }, [user, navigate, from]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && password !== confirm) {
      setError(t("Les deux mots de passe ne correspondent pas"));
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success(t("Bon retour sur GoSpot !"));
      } else {
        await register(name, email, password);
        toast.success(t("Bienvenue sur GoSpot !"));
      }
      navigate(from);
    } catch (err) {
      setError(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="login-page" className="mx-auto flex max-w-md flex-col items-center px-4 py-16 sm:py-24">
      <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2563EB] to-[#06B6D4]">
        <MapPin className="h-6 w-6 text-white" />
      </div>
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
        {mode === "login" ? t("Bon retour !") : t("Crée ton compte")}
      </h1>
      <p className="mt-2 text-sm text-slate-500">
        {mode === "login" ? t("Connecte-toi pour enregistrer tes spots favoris.") : t("Rejoins la communauté GoSpot.")}
      </p>

      <div className="mt-6 flex w-full rounded-full bg-slate-100 p-1">
        {[
          { id: "login", label: t("Connexion") },
          { id: "register", label: t("Inscription") },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            data-testid={`tab-${tab.id}`}
            onClick={() => {
              setMode(tab.id);
              setError("");
            }}
            className={`flex-1 rounded-full py-2 text-sm font-semibold transition ${
              mode === tab.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-6 w-full space-y-3">
        {mode === "register" && (
          <input
            data-testid="register-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("Ton nom")}
            required
            className={inputClass}
          />
        )}
        <input
          data-testid="login-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("Courriel")}
          required
          className={inputClass}
        />
        <PasswordInput
          testId="login-password-input"
          toggleTestId="toggle-password-visibility"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("Mot de passe (6 caractères min.)")}
          autoComplete={mode === "login" ? "current-password" : "new-password"}
        />
        {mode === "register" && (
          <PasswordInput
            testId="register-confirm-input"
            toggleTestId="toggle-confirm-visibility"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={t("Confirme ton mot de passe")}
            autoComplete="new-password"
          />
        )}
        {error && (
          <p data-testid="login-error" className="rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">
            {error}
          </p>
        )}
        {mode === "login" && (
          <Link to="/reinitialiser-mot-de-passe" data-testid="forgot-password-link" className="block text-center text-xs font-semibold text-[#2563EB] hover:underline">
            {t("Mot de passe oublié ?")}
          </Link>
        )}
        <button
          type="submit"
          data-testid="login-form-submit-button"
          disabled={loading}
          className="w-full rounded-full bg-[#2563EB] py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1D4ED8] active:scale-[0.98] disabled:opacity-60"
        >
          {loading ? t("Un instant…") : mode === "login" ? t("Se connecter") : t("Créer mon compte")}
        </button>
      </form>
    </main>
  );
}
